import React, { useState } from 'react';
import { Sparkles as BotIcon, User, Copy, Check, Sparkles, ArrowRight, Wand2, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  TripPlanCard,
  TransportList,
  AlternativeCard,
  InsiderTipsCard,
  HotelList,
  FlightList,
} from './ToolCard';
import AlternativesStrip from './AlternativesStrip';
import FlightAlternatesRail from './FlightAlternatesRail';
import TravelCompanion from './TravelCompanion';
import ShareTripButton from './ShareTripButton';
import GroundTransportSection from './GroundTransportSection';

/**
 * sanitizeBubbleText — iter66 hot-fix safety net.
 *
 * If the AI message ever contains a leaked JSON envelope (e.g. backend
 * sanitiser missed a variant), replace it with polite prose so the user
 * never sees raw `{ "tripPlan": ... }` text in a chat bubble.
 *
 * Trip cards still render from the structured `tripPlan` / `hotels` /
 * `flights` props alongside the bubble — only the message *text* is
 * sanitised here.
 */
function sanitizeBubbleText(text, language) {
  if (typeof text !== 'string') return text || '';
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) return text;
  const structuralTokens = ['"tripPlan"', '"trip_plan"', '"flights"', '"hotels"',
    '"alternatives"', '"insiderTips"', '"infoMode"'];
  const leaked = structuralTokens.some((tok) => trimmed.includes(tok));
  if (!leaked) return text;
  console.warn('[bubble] sanitised leaked JSON envelope from message text');
  const isBg = (language || 'en').toLowerCase().startsWith('bg');
  return isBg
    ? 'Готово! Виж предложенията по-долу.'
    : 'Done — check the options below.';
}

/**
 * MessageBubble — renders ONE message in the AI chat scroll.
 *
 * For user messages: blue/cyan filled bubble, right-aligned, no tool cards.
 * For AI messages:    white card with a copy button, optionally followed
 *                     by tool cards (TripPlan, Transports, Alternative,
 *                     InsiderTips, Hotels, Flights), refinement quick-actions
 *                     and structured suggestion chips.
 *
 * Props:
 *   message        — { id, text, isUser, timestamp, tripPlan?, hotels?,
 *                       flights?, insiderTips?, hotelSearchContext?,
 *                       suggestions? }
 *   language       — current EN/BG locale (forwarded to date helpers)
 *   loading        — disables suggestion + quick-action buttons during request
 *   copiedId       — id of the message whose copy button was just pressed
 *   onCopy(id,text) — clipboard handler
 *   onBookHotel(hotel) — opens hotel checkout preview / auth gate
 *   onBookFlight(flight) — opens flight redirect / auth gate
 *   onSendQuery(text) — sends a follow-up message (used by quick-actions)
 *   onSuggestionClick(suggestion) — handles structured suggestions
 *   actionConfig   — { 'action:search_flights': {label, target}, … }
 *   quickActions   — array of refinement chips (already translated)
 */
export default function MessageBubble({
  message,
  language,
  loading,
  copiedId,
  onCopy,
  onBookHotel,
  onBookFlight,
  onSendQuery,
  onSuggestionClick,
  actionConfig,
  quickActions,
}) {
  const { t } = useLanguage();
  const hasTools = (message.hotels && message.hotels.length > 0) ||
                   (message.flights && message.flights.length > 0);
  // Progressive disclosure: AI text over 480 chars collapses to a
  // ~320-char summary with a "Read more" toggle. Only applies to AI
  // messages that also have tool cards below (users only need enough
  // context to jump into the cards; the full prose is one tap away).
  const cleanedText = sanitizeBubbleText(message.text, language);
  const LONG_TEXT_THRESHOLD = 480;
  const isLongText = !message.isUser && hasTools && typeof cleanedText === 'string' && cleanedText.length > LONG_TEXT_THRESHOLD;
  const [expanded, setExpanded] = useState(false);
  const displayText = isLongText && !expanded
    ? (() => {
        // Snap the trim to the previous sentence-end / newline so the
        // "Read more" click reveals the natural continuation.
        const cutoff = cleanedText.slice(0, 320);
        const lastBreak = Math.max(cutoff.lastIndexOf('.'), cutoff.lastIndexOf('\n'), cutoff.lastIndexOf('!'), cutoff.lastIndexOf('?'));
        return (lastBreak > 200 ? cutoff.slice(0, lastBreak + 1) : cutoff) + '…';
      })()
    : cleanedText;

  return (
    <div className={`flex gap-2.5 ${message.isUser ? 'justify-end' : 'justify-start'}`}>
      {!message.isUser && (
        <div className="w-7 h-7 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
          <BotIcon className="w-3.5 h-3.5 text-white" />
        </div>
      )}

      <div className={`max-w-[85%] sm:max-w-[80%] ${message.isUser ? 'order-first' : ''} group`}>
        <div
          className={`px-4 py-3 rounded-2xl relative ${
            message.isUser
              ? 'bg-gray-900 text-white rounded-br-md shadow-sm'
              : 'bg-white text-gray-900 border border-gray-100 rounded-bl-md shadow-sm'
          }`}
        >
          {/* Streaming phase pill — visible only on the in-flight AI
              message before any text lands. Gives the user instant
              feedback ("Analyzing your request…") instead of an
              opaque spinner. Replaced by the AI's text once the
              trip_plan event arrives. */}
          {message.isStreaming && !message.text && (
            <div
              className="flex items-center gap-2 text-sm text-gray-500"
              data-testid={`chat-streaming-${message.id}`}
            >
              <span className="relative inline-flex">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-ping absolute" />
                <span className="w-2 h-2 bg-blue-500 rounded-full relative" />
              </span>
              <span>
                {message.streamingPhase === 'calling_ai'
                  ? t('chat.streamBuildingPlan') || 'Building your itinerary…'
                  : message.streamingPhase === 'rendering'
                  ? t('chat.streamFinalizing') || 'Finalizing details…'
                  : t('chat.streamAnalyzing') || 'Analyzing your request…'}
              </span>
              {message.streamingMeta?.discovery && (
                <span
                  className="text-[10px] uppercase tracking-wide text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full"
                  title="Discovery mode active"
                >
                  Hidden gems
                </span>
              )}
              {message.streamingMeta?.hasBudgetBlock && (
                <span
                  className="text-[10px] uppercase tracking-wide text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full"
                  title="Budget intelligence active"
                >
                  Budget
                </span>
              )}
            </div>
          )}
          <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid={`message-text-${message.id}`}>
            {displayText}
          </p>
          {isLongText && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`chat-read-more-${message.id}`}
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 transition-colors"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  {language === 'bg' ? 'Свий' : 'Show less'}
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  {language === 'bg' ? 'Прочети още' : 'Read more'}
                </>
              )}
            </button>
          )}
          {/* PHASE 1 #3 — Fallback recovery banner. Shown only when the
              backend's AI quota was exhausted (or the LLM call failed)
              and the response was synthesised from the curated DB.
              Tells the user honestly: "we're refining your options"
              without scaring them with raw error text. */}
          {message.fallbackMode && (
            <div
              className="mt-3 inline-flex items-start gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5 rounded-md"
              data-testid={`fallback-banner-${message.id}`}
            >
              <span className="text-amber-600 leading-none mt-0.5">●</span>
              <span>
                {language === 'bg'
                  ? 'Усъвършенстваме AI селекцията. Алтернативите по-долу са проверени.'
                  : 'Refining the AI selection — the alternatives below are verified picks.'}
              </span>
            </div>
          )}
          {/* Trip-window banner — single line showing the unified
              outbound/return / check-in/check-out window so the user
              sees flight + hotel are date-synchronised at a glance.
              Renders only when the backend supplied a structured
              tripWindow object (single-destination trips). */}
          {message.tripWindow?.checkIn && message.tripWindow?.checkOut && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-xs bg-blue-50 border border-blue-200 text-blue-900 px-2.5 py-1 rounded-md"
              data-testid={`trip-window-banner-${message.id}`}
              title={language === 'bg' ? 'Полети и хотел са синхронизирани' : 'Flights and hotel are date-synchronised'}
            >
              <Calendar className="w-3 h-3" />
              <span className="font-semibold">
                {language === 'bg' ? 'Твоето пътуване:' : 'Your trip:'}
              </span>
              <span>
                {message.tripWindow.checkIn} → {message.tripWindow.checkOut}
              </span>
              <span className="text-blue-700/70">
                · {message.tripWindow.nights}{' '}
                {language === 'bg' ? 'нощувки' : 'nights'}
              </span>
            </div>
          )}
          {!message.isUser && message.text && (
            <button
              onClick={() => onCopy(message.id, message.text)}
              className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-gray-200 shadow-sm text-gray-500 hover:text-blue-600 hover:border-blue-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`chat-copy-${message.id}`}
              title={copiedId === message.id ? t('chat.copied') : t('chat.copy')}
              aria-label={t('chat.copy')}
            >
              {copiedId === message.id ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        {/* Tool cards — order matters (visual hierarchy).
            Sprint A5: each section fades up sequentially so the tripPlan
            "streams" into view like a concierge writing a plan. 300ms
            cascade per section. motion-safe wrapper honors reduced-motion. */}
        {(() => {
          // Stagger order matches the visual funnel: place → transport →
          // alternative → tips → hotels → flights → ground → adjacent.
          const stagger = (i) => ({
            className: 'motion-safe:animate-fade-up',
            style: { animationDelay: `${i * 220}ms`, animationFillMode: 'both' },
          });
          return (
            <>
              <div {...stagger(0)}>
                <TripPlanCard tripPlan={message.tripPlan} />
                {message.tripPlan?.destination && (
                  <div className="mt-2 mb-3 flex justify-end">
                    <ShareTripButton
                      tripPlan={message.tripPlan}
                      hotels={message.hotels}
                      flights={message.flights}
                      message={message.text}
                    />
                  </div>
                )}
              </div>
              <div {...stagger(1)}>
                <TransportList transports={message.tripPlan?.transports} messageId={message.id} />
                <AlternativeCard alternative={message.tripPlan?.alternative} messageId={message.id} />
                <InsiderTipsCard tips={message.insiderTips} messageId={message.id} />
              </div>
              <div {...stagger(2)}>
                <HotelList
                  hotels={message.hotels}
                  searchContext={message.hotelSearchContext}
                  language={language}
                  onBookHotel={onBookHotel}
                />
              </div>
              <div {...stagger(3)}>
                <FlightList
                  flights={message.flights}
                  language={language}
                  onBookFlight={onBookFlight}
                />
              </div>
              <div {...stagger(4)}>
                <GroundTransportSection groundTransport={message.groundTransport} />
              </div>
            </>
          );
        })()}

        {/* iter56 — adjacent-date flight alternatives ("Fly 2 days
            later for €60 less / Switch to a direct flight"). Renders
            only when find_better_adjacent_dates found something. */}
        <FlightAlternatesRail
          alternates={message.flightAlternates}
          onPick={(alt) => {
            const human = alt.label || `Switch to ${alt.date}`;
            const followUp = alt.returnDate
              ? `Use ${alt.date} → ${alt.returnDate} instead — ${human}.`
              : `Use ${alt.date} instead — ${human}.`;
            onSendQuery && onSendQuery(followUp);
          }}
        />

        {/* "Other options to consider" strip — only when AI surfaced
            alternatives (vague-prompt multi-option mode). Tapping any
            alternative sends a follow-up "Plan this trip: …" query so
            the AI generates a full plan for that pick. */}
        <AlternativesStrip
          alternatives={message.alternatives}
          onPick={(alt) => {
            const dest = alt.destination || alt.destinationEn;
            const nights = alt.durationNights ? `${alt.durationNights} nights` : '';
            const total = alt.totalEstimate ? `budget ${alt.totalEstimate}` : '';
            const followUp = [
              `Plan this trip: ${dest}`,
              nights,
              total,
            ].filter(Boolean).join(', ');
            onSendQuery && onSendQuery(followUp);
          }}
        />

        {/* iter59 — Travel Companion sections (Why / Things / Beaches /
            Food / Transport / Mistakes) + Daily Plan CTA. Renders only
            when the backend `companion` payload is present OR when a
            tripPlan is available for the daily-plan button. */}
        <TravelCompanion
          companion={message.companion}
          dailyPlanAvailable={message.dailyPlanAvailable}
          destination={message.tripPlan?.destination || message.tripPlan?.destinationEn}
          nights={message.tripPlan?.nights}
          onSendQuery={onSendQuery}
        />

        {/* Quick-action refinement chips — only when tools were returned */}
        {!message.isUser && hasTools && (
          <div className="mt-3" data-testid={`chat-quick-actions-${message.id}`}>
            <div className="flex items-center gap-1.5 mb-1.5 text-[11px] uppercase tracking-wider text-gray-500 font-semibold px-1">
              <Wand2 className="w-3 h-3" />
              {t('chat.refineTitle')}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {quickActions.map((action) => (
                <button
                  key={`${message.id}-qa-${action}`}
                  onClick={() => onSendQuery(action)}
                  disabled={loading}
                  data-testid={`chat-quick-action-${action.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
                  className="px-3 py-1 text-xs bg-slate-100 border border-slate-200 text-slate-700 rounded-full hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all disabled:opacity-50 font-medium"
                >
                  {action}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick Suggestions */}
        {message.suggestions && message.suggestions.length > 0 && !message.isUser && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.suggestions.map((suggestion) => {
              const action = actionConfig[suggestion];
              const label = action ? action.label : suggestion;
              const Icon = action ? ArrowRight : Sparkles;
              return (
                <button
                  key={`${message.id}-suggestion-${suggestion}`}
                  onClick={() => onSuggestionClick(suggestion)}
                  disabled={loading}
                  className={`px-3.5 py-1.5 text-xs rounded-full border transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center gap-1 ${
                    action
                      ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600 shadow-sm shadow-blue-600/20'
                      : 'bg-white hover:bg-blue-50 text-blue-700 border-blue-200 hover:border-blue-300'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {label}
                </button>
              );
            })}
          </div>
        )}

        <p className="text-xs text-gray-400 mt-2 px-1">
          {message.timestamp.toLocaleTimeString()}
        </p>
      </div>

      {message.isUser && (
        <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
          <User className="w-3.5 h-3.5 text-gray-600" />
        </div>
      )}
    </div>
  );
}
